import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  Loading,
  Modal,
  PageHeader,
  Textarea,
  Toast,
} from '@/components/ui';

interface Service {
  id: string;
  key: string;
  name: string;
  category: string;
  aliases: string[];
  description: string | null;
  qualification: {
    questions?: string[];
    required_fields?: string[];
    score_weight?: number;
  };
  sort_order: number;
  active: boolean;
}

export default function ServicesCatalog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Service | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['admin-services'],
    queryFn: () => api.get<{ data: Service[] }>('/admin/services'),
  });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/admin/services/${id}`, { active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-services'] });
      setToast({ message: 'Service updated', tone: 'good' });
    },
    onError: (err) =>
      setToast({
        message: errorMessage(err, 'Could not update'),
        tone: 'bad',
      }),
  });

  if (isPending) return <Loading />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  return (
    <div className="w-full">
      <PageHeader
        title="Services catalog"
        description="The Home Services verticals the one universal bot understands."
        actions={<Button onClick={() => setOpen(true)}>Add service</Button>}
      />

      <Card className="mb-4">
        <p className="text-sm text-ink-600 dark:text-ink-300">
          <strong className="text-ink-900 dark:text-ink-50">This is the product surface.</strong>{' '}
          Every vertical below is handled by the same AI engine, in the same conversation. A
          customer can raise two of them in one call and the bot carries both. Adding a new
          vertical is a row here — never a new bot and never a new code path.
        </p>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2">
        {data.data.map((service) => (
          <Card key={service.id}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-ink-900 dark:text-ink-50">{service.name}</h3>
                  <Badge tone={service.active ? 'green' : 'gray'}>
                    {service.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <p className="font-mono text-xs text-ink-500">{service.key}</p>
              </div>
              <div className="flex shrink-0 gap-1.5">
                <Button size="sm" variant="secondary" onClick={() => setEditing(service)}>
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => toggle.mutate({ id: service.id, active: !service.active })}
                >
                  {service.active ? 'Disable' : 'Enable'}
                </Button>
              </div>
            </div>

            {service.description && (
              <p className="mt-2 text-sm text-ink-600 dark:text-ink-300">
                {service.description}
              </p>
            )}

            {service.aliases.length > 0 && (
              <div className="mt-2">
                <p className="text-[10px] uppercase tracking-wide text-ink-400">
                  Detected from
                </p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {service.aliases.slice(0, 8).map((alias) => (
                    <span
                      key={alias}
                      className="rounded bg-ink-100 px-1.5 py-0.5 font-mono text-[11px] text-ink-600 dark:bg-ink-800 dark:text-ink-300"
                    >
                      {alias}
                    </span>
                  ))}
                  {service.aliases.length > 8 && (
                    <span className="text-[11px] text-ink-400">
                      +{service.aliases.length - 8}
                    </span>
                  )}
                </div>
              </div>
            )}

            {(service.qualification?.questions ?? []).length > 0 && (
              <div className="mt-2">
                <p className="text-[10px] uppercase tracking-wide text-ink-400">
                  Vertical questions
                </p>
                <ul className="mt-1 space-y-0.5">
                  {service.qualification.questions!.map((question) => (
                    <li key={question} className="text-xs text-ink-600 dark:text-ink-300">
                      · {question}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-ink-500">
              {service.qualification?.score_weight !== undefined && (
                <span>weight +{service.qualification.score_weight}</span>
              )}
              {(service.qualification?.required_fields ?? []).length > 0 && (
                <span>
                  requires {service.qualification.required_fields!.join(', ')}
                </span>
              )}
            </div>
          </Card>
        ))}
      </div>

      {(open || editing) && (
        <ServiceModal
          service={editing}
          onClose={() => {
            setOpen(false);
            setEditing(null);
          }}
          onDone={() => {
            setOpen(false);
            setEditing(null);
            queryClient.invalidateQueries({ queryKey: ['admin-services'] });
            setToast({ message: 'Service saved', tone: 'good' });
          }}
          onError={(message) => setToast({ message, tone: 'bad' })}
        />
      )}

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}

function ServiceModal({
  service,
  onClose,
  onDone,
  onError,
}: {
  service: Service | null;
  onClose: () => void;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const [key, setKey] = useState(service?.key ?? '');
  const [name, setName] = useState(service?.name ?? '');
  const [description, setDescription] = useState(service?.description ?? '');
  const [aliases, setAliases] = useState((service?.aliases ?? []).join(', '));
  const [questions, setQuestions] = useState(
    (service?.qualification?.questions ?? []).join('\n'),
  );
  const [requiredFields, setRequiredFields] = useState(
    (service?.qualification?.required_fields ?? []).join(', '),
  );
  const [weight, setWeight] = useState(String(service?.qualification?.score_weight ?? 0));
  const [sortOrder, setSortOrder] = useState(String(service?.sort_order ?? 100));

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        description: description || null,
        aliases: aliases
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean),
        qualification: {
          questions: questions.split('\n').map((q) => q.trim()).filter(Boolean),
          required_fields: requiredFields
            .split(',')
            .map((f) => f.trim())
            .filter(Boolean),
          score_weight: Number(weight) || 0,
        },
        sort_order: Number(sortOrder) || 100,
      };
      return service
        ? api.patch(`/admin/services/${service.id}`, payload)
        : api.post('/admin/services', { ...payload, key, active: true });
    },
    onSuccess: onDone,
    onError: (err) => onError(errorMessage(err, 'Could not save')),
  });

  return (
    <Modal
      open
      wide
      onClose={onClose}
      title={service ? `Edit ${service.name}` : 'Add a service'}
      description="Handled by the same universal engine as every other vertical."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!name || (!service && !key)}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          {!service && (
            <Field label="Key" required hint="Lowercase, underscores. Immutable once set.">
              <Input
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="gutter_cleaning"
              />
            </Field>
          )}
          <Field label="Display name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Sort order" hint="Lower appears first in the catalog.">
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </Field>
          <Field label="Score weight" hint="Added to the lead score when this service is confirmed.">
            <Input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} />
          </Field>
        </div>

        <Field label="Description">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        <Field
          label="Aliases"
          hint="Comma separated. These are what the detector matches in speech — include how customers actually say it."
        >
          <Textarea
            rows={3}
            value={aliases}
            onChange={(e) => setAliases(e.target.value)}
            placeholder="gutters, gutter cleaning, downspouts"
          />
        </Field>

        <Field label="Vertical questions" hint="One per line. Woven into the conversation when this service is identified.">
          <Textarea rows={4} value={questions} onChange={(e) => setQuestions(e.target.value)} />
        </Field>

        <Field
          label="Extra required fields"
          hint="Comma separated. Added on top of the bot's own required fields when this service is present."
        >
          <Input
            value={requiredFields}
            onChange={(e) => setRequiredFields(e.target.value)}
            placeholder="existing_issue"
          />
        </Field>
      </div>
    </Modal>
  );
}
